import { useState, useEffect } from 'react';
import { api } from '../../lib/api';
import {
  Users, ChevronLeft, ChevronRight, AlertTriangle,
  Shield, UserX, UserCheck, Trash2
} from 'lucide-react';

interface PlatformUser {
  id: string;
  email: string;
  full_name: string;
  role: string;
  status: string;
  desired_site_name?: string;
  created_at: string;
}

const PAGE_SIZE = 15;

const statusColors: Record<string, string> = {
  waitlist: 'bg-blue-100 text-blue-700',
  pending: 'bg-blue-100 text-blue-700',
  approved: 'bg-green-100 text-green-700',
  active: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
  blocked: 'bg-gray-100 text-gray-600',
};

const statusLabels: Record<string, string> = {
  waitlist: 'Wachtlijst',
  pending: 'Wachtend',
  approved: 'Goedgekeurd',
  active: 'Actief',
  rejected: 'Afgewezen',
  blocked: 'Geblokkeerd',
};

export default function UsersPage() {
  const [users, setUsers] = useState<PlatformUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [confirmDelete, setConfirmDelete] = useState<PlatformUser | null>(null);

  useEffect(() => {
    api.get<PlatformUser[]>('/users')
      .then(setUsers)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  async function handleStatusChange(userId: string, newStatus: string) {
    try {
      await api.put(`/users/${userId}`, { status: newStatus });
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, status: newStatus } : u));
      setSuccess(`Status gewijzigd naar ${statusLabels[newStatus] || newStatus}`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (e: any) {
      setError(e.message);
    }
  }

  async function handleDelete(userId: string) {
    try {
      await api.delete(`/users/${userId}`);
      setUsers(prev => prev.filter(u => u.id !== userId));
      setConfirmDelete(null);
      setSuccess('Gebruiker verwijderd');
      setTimeout(() => setSuccess(''), 3000);
    } catch (e: any) {
      setError(e.message);
      setConfirmDelete(null);
    }
  }

  const filtered = users.filter(u => {
    if (!search) return true;
    const q = search.toLowerCase();
    return u.full_name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q);
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => { setPage(1); }, [search]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Gebruikers</h1>
        <p className="text-sm text-gray-500 mt-1">Beheer platform gebruikers ({users.length})</p>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          <AlertTriangle className="w-4 h-4" /> {error}
        </div>
      )}
      {success && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">{success}</div>
      )}

      <div className="card p-4">
        <input
          type="text"
          placeholder="Zoek op naam of e-mail..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="input w-full sm:w-72"
        />
      </div>

      <div className="card overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Gebruiker</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Rol</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Datum</th>
              <th className="px-6 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {loading ? (
              [...Array(5)].map((_, i) => (
                <tr key={i}>{[...Array(5)].map((__, j) => <td key={j} className="px-6 py-4"><div className="h-4 bg-gray-100 rounded animate-pulse" /></td>)}</tr>
              ))
            ) : paginated.length === 0 ? (
              <tr><td colSpan={5} className="px-6 py-12 text-center text-sm text-gray-400">Geen gebruikers gevonden.</td></tr>
            ) : paginated.map(u => (
              <tr key={u.id} className="hover:bg-gray-50">
                <td className="px-6 py-4">
                  <p className="text-sm font-medium text-gray-900">{u.full_name || '-'}</p>
                  <p className="text-xs text-gray-400">{u.email}</p>
                  {u.desired_site_name && <p className="text-xs text-gray-400 mt-0.5">Site: {u.desired_site_name}</p>}
                </td>
                <td className="px-6 py-4">
                  <span className="flex items-center gap-1 text-sm text-gray-700">
                    {u.role === 'super_admin' && <Shield className="w-3.5 h-3.5 text-brand-500" />}
                    {u.role === 'super_admin' ? 'Super Admin' : 'Tenant'}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[u.status] ?? 'bg-gray-100 text-gray-600'}`}>
                    {statusLabels[u.status] ?? u.status}
                  </span>
                </td>
                <td className="px-6 py-4 text-sm text-gray-500">
                  {u.created_at ? new Date(u.created_at).toLocaleDateString('nl-NL') : '-'}
                </td>
                <td className="px-6 py-4 text-right">
                  <div className="flex items-center justify-end gap-1">
                    {u.status === 'blocked' ? (
                      <button
                        onClick={() => handleStatusChange(u.id, 'active')}
                        className="p-1.5 text-green-500 hover:bg-green-50 rounded-lg transition-colors"
                        title="Deblokkeren"
                      >
                        <UserCheck className="w-4 h-4" />
                      </button>
                    ) : u.role !== 'super_admin' && (
                      <button
                        onClick={() => handleStatusChange(u.id, 'blocked')}
                        className="p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                        title="Blokkeren"
                      >
                        <UserX className="w-4 h-4" />
                      </button>
                    )}
                    {u.role !== 'super_admin' && (
                      <button
                        onClick={() => setConfirmDelete(u)}
                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Verwijderen"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-3 border-t border-gray-100">
            <span className="text-xs text-gray-400">{filtered.length} gebruiker{filtered.length !== 1 ? 's' : ''}</span>
            <div className="flex items-center gap-1">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-xs text-gray-500 px-2">{page} / {totalPages}</span>
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Delete confirmation modal */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Gebruiker verwijderen?</h3>
                <p className="text-sm text-gray-500">Dit kan niet ongedaan worden gemaakt.</p>
              </div>
            </div>
            <p className="text-sm text-gray-600 mb-5">
              <strong>{confirmDelete.full_name}</strong> ({confirmDelete.email}) wordt permanent verwijderd van het platform.
            </p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setConfirmDelete(null)} className="btn-secondary text-sm">
                Annuleren
              </button>
              <button
                onClick={() => handleDelete(confirmDelete.id)}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
              >
                Verwijderen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
